import React, { useState, useRef, useEffect } from 'react';
import { useAgentConversation } from '../../hooks/useAgentConversation';
import type { AgentMachineContext, HubIdentity } from '../../hooks/agentMachineContext';
import ChatMessage from './ChatMessage';
import SuggestedActions from './SuggestedActions';
import CartPanel from './CartPanel';
import './AgentChat.scss';

interface AgentChatProps {
  organizationId: string;
  organizationName?: string;
  primaryColor?: string;
  embedded?: boolean; // true = floating bubble, false = inline full page
  machineContext?: AgentMachineContext | null; // machine QR hub: the sticker's machine
  hubIdentity?: HubIdentity | null; // machine QR hub: the verified session, null when unverified
}

const AgentChat: React.FC<AgentChatProps> = ({
  organizationId,
  organizationName = 'our team',
  primaryColor,
  embedded = true,
  machineContext,
  hubIdentity,
}) => {
  const {
    messages,
    activeAgent,
    loading,
    error,
    isComplete,
    suggestedActions,
    cart,
    sendMessage,
    submitCustomerInfoForm,
    submitVerificationCode,
    skipVerification,
    resendVerificationCode,
    endConversation,
    clearError,
  } = useAgentConversation(organizationId, { machineContext, hubIdentity });

  const [isOpen, setIsOpen] = useState(!embedded);
  const [userInput, setUserInput] = useState('');
  const messagesRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Apply primary color as CSS variable
  useEffect(() => {
    if (primaryColor) {
      document.documentElement.style.setProperty('--agent-primary-color', primaryColor);
    }
  }, [primaryColor]);

  // Scroll to bottom when messages change. Only the list scrolls: scrollIntoView also scrolls the page
  // wherever the chat sits in flow (the machine QR hub), shoving the masthead off screen every turn.
  useEffect(() => {
    const list = messagesRef.current;
    list?.scrollTo({ top: list.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);

  // Phones: the on-screen keyboard shrinks only the visual viewport (iOS leaves 100vh/svh alone), so a window
  // sized from those keeps the composer under the keyboard. Mirror the visual viewport into CSS vars the
  // window heights read, and keep the focused composer in view while it changes.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const root = document.documentElement.style;
    const sync = () => {
      root.setProperty('--agent-vvh', `${Math.round(vv.height)}px`);
      root.setProperty('--agent-vvt', `${Math.round(vv.offsetTop)}px`);
      if (document.activeElement === inputRef.current) inputRef.current?.scrollIntoView({ block: 'nearest' });
    };
    sync();
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    return () => { vv.removeEventListener('resize', sync); vv.removeEventListener('scroll', sync); root.removeProperty('--agent-vvh'); root.removeProperty('--agent-vvt'); };
  }, []);

  // Focus input when chat opens; never by scrolling the page (the hub's chat is in flow).
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
    }
  }, [isOpen]);

  // Auto-resize textarea; border-box, so add the border or it clips the last line by 2px
  useEffect(() => {
    const textarea = inputRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight + textarea.offsetHeight - textarea.clientHeight}px`;
  }, [userInput]);

  const handleSend = async (text?: string) => {
    const msg = (text || userInput).trim();
    if (!msg || loading || isComplete) return;
    setUserInput('');
    await sendMessage(msg);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestionClick = (action: string) => {
    handleSend(action);
  };

  const handleEndChat = async () => {
    await endConversation();
  };

  const toggleOpen = () => {
    setIsOpen(prev => !prev);
  };

  // Expanded = larger window so cards/forms are easier to read; sizes in SCSS.
  const [isExpanded, setIsExpanded] = useState(false);

  const agentLabel = activeAgent === 'general' ? 'AI Assistant' :
    activeAgent === 'parts' ? 'Parts Specialist' :
    activeAgent === 'service' ? 'Service Specialist' :
    activeAgent === 'sales' ? 'Equipment Specialist' : 'AI Assistant';

  const chatWindow = (
    <div className={`agent-chat__window ${embedded ? 'agent-chat__window--floating' : 'agent-chat__window--inline'}${isExpanded ? ' agent-chat__window--expanded' : ''}`}>
      {/* Header */}
      <div className="agent-chat__header">
        <div className="agent-chat__header-info">
          <div className="agent-chat__header-avatar">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <div>
            <div className="agent-chat__header-title">{agentLabel}</div>
            <div className="agent-chat__header-subtitle">{organizationName}</div>
          </div>
        </div>
        <div className="agent-chat__header-actions">
          {!isComplete && messages.length > 0 && (
            <button
              className="agent-chat__header-btn agent-chat__header-btn--end"
              onClick={handleEndChat}
              title="End conversation"
              type="button"
            >
              End
            </button>
          )}
          <button
            className="agent-chat__header-btn"
            onClick={() => setIsExpanded(prev => !prev)}
            title={isExpanded ? 'Shrink window' : 'Expand window'}
            type="button"
          >
            {isExpanded ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="4 14 10 14 10 20" />
                <polyline points="20 10 14 10 14 4" />
                <line x1="14" y1="10" x2="21" y2="3" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 3 21 3 21 9" />
                <polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            )}
          </button>
          <button
            className="agent-chat__header-btn"
            onClick={toggleOpen}
            title="Minimize"
            type="button"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </div>
      </div>

      {/* Cart panel (parts specialist only, when cart has items) */}
      {activeAgent === 'parts' && !isComplete && (
        <CartPanel
          cart={cart}
          disabled={loading}
          onSendMessage={(msg) => handleSend(msg)}
        />
      )}

      {/* Messages */}
      <div className="agent-chat__messages" ref={messagesRef}>
        {messages.length === 0 && !loading && (
          <div className="agent-chat__welcome">
            <div className="agent-chat__welcome-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <p className="agent-chat__welcome-text">
              Hi! How can I help you today?
            </p>
          </div>
        )}

        {messages.map((msg) => (
          <ChatMessage
            key={msg.id}
            message={msg}
            orgName={organizationName}
            disabled={isComplete}
            onAskAboutEquipment={(title, stockNumber) => {
              setUserInput(`Tell me more about the ${title} (Stock #${stockNumber})`);
              inputRef.current?.focus();
            }}
            onAddToCart={(partNumber) => {
              handleSend(`Add 1 ${partNumber} to my cart`);
            }}
            onSendMessage={(text) => handleSend(text)}
            onSubmitCustomerInfoForm={(values) => submitCustomerInfoForm(values)}
            onSubmitVerificationCode={(code) => submitVerificationCode(code)}
            onSkipVerification={() => skipVerification()}
            onResendVerificationCode={() => resendVerificationCode()}
          />
        ))}

        {loading && (
          <div className="agent-chat__message agent-chat__message--assistant">
            <div className="agent-chat__message-bubble">
              <div className="agent-chat__typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="agent-chat__error">
          <span>{error}</span>
          <button onClick={clearError} type="button">x</button>
        </div>
      )}

      {/* Initial suggestions when no messages yet */}
      {!isComplete && !loading && messages.length === 0 && (
        <SuggestedActions
          actions={suggestedActions}
          onSelect={handleSuggestionClick}
          disabled={loading}
        />
      )}

      {/* Input */}
      {!isComplete ? (
        <form className="agent-chat__input-area" onSubmit={(e) => { e.preventDefault(); handleSend(); }}>
          <textarea
            ref={inputRef}
            className="agent-chat__input"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={loading ? 'Thinking...' : 'Type a message...'}
            disabled={loading}
            rows={1}
          />
          <button
            className="agent-chat__send-btn"
            type="submit"
            disabled={!userInput.trim() || loading}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </form>
      ) : (
        <div className="agent-chat__complete-bar">
          <span>Conversation ended</span>
        </div>
      )}

      {/* Powered by */}
      <div className="agent-chat__powered-by">
        Powered by <a href="https://opervia.com" target="_blank" rel="noopener noreferrer">Opervia</a>
      </div>
    </div>
  );

  const triggerIcon = (
    <img
      src="https://form.opervia.com/opervia-icon.png"
      alt=""
      className="agent-chat__trigger-icon"
      onError={(e) => { (e.target as HTMLImageElement).src = '/opervia-icon.png'; }}
    />
  );

  // Embedded mode: trigger pill + expandable window
  if (embedded) {
    return (
      <div className="agent-chat">
        {isOpen && chatWindow}
        {isOpen ? (
          <button
            className="agent-chat__bubble agent-chat__bubble--close"
            onClick={toggleOpen}
            type="button"
            aria-label="Close chat"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        ) : (
          <button
            className="agent-chat__trigger"
            onClick={toggleOpen}
            type="button"
            aria-label="Open chat"
          >
            {triggerIcon}
            <span className="agent-chat__trigger-text">How can I help?</span>
          </button>
        )}
      </div>
    );
  }

  // Inline mode: collapsible with trigger pill
  return (
    <div className="agent-chat agent-chat--inline">
      {isOpen && chatWindow}
      {!isOpen && (
        <button
          className="agent-chat__trigger"
          onClick={toggleOpen}
          type="button"
          aria-label="Open chat"
        >
          {triggerIcon}
          <span className="agent-chat__trigger-text">How can I help?</span>
        </button>
      )}
    </div>
  );
};

export default AgentChat;
